// Progressionstilpasset hastighedstest (variant B: kun solidt stof).
// Tast-sæt og ord kommer kun fra gennemførte trin — ikke det aktive trin.

import { LEVEL_6_VALID, LEVEL_6_KEYS } from '../data/curriculum/grundniveau';
import {
  NIVEAU_1_SPRINT_WORDS,
  NIVEAU_1_UNLOCKED_KEYS,
} from '../data/curriculum/niveau-1';
import {
  NIVEAU_2_SPRINT_WORDS,
  NIVEAU_2_UNLOCKED_KEYS,
} from '../data/curriculum/niveau-2';
import {
  NIVEAU_3_SPRINT_WORDS,
  NIVEAU_3_UNLOCKED_KEYS,
} from '../data/curriculum/niveau-3';
import {
  NIVEAU_4_ALPHABET_WORDS,
  NIVEAU_4_UNLOCKED_KEYS,
} from '../data/curriculum/niveau-4';
import { getTrin } from '../data/curriculum';
import { interleaved } from '../data/curriculum/generators';
import { textUsesOnlyKeys } from './dictationUnits';
import type { BaselineSnapshot, UserProfile, WpmCheckIn } from '../types';

const TIER_ORDER = [
  'grundniveau',
  'niveau-1',
  'niveau-2',
  'niveau-3',
  'niveau-4',
] as const;

const WORD_SOURCES: Record<
  string,
  { chunks: string[]; weight: number }
> = {
  grundniveau: { chunks: LEVEL_6_VALID, weight: 1 },
  'niveau-1': { chunks: NIVEAU_1_SPRINT_WORDS, weight: 1 },
  'niveau-2': { chunks: NIVEAU_2_SPRINT_WORDS, weight: 1 },
  'niveau-3': { chunks: NIVEAU_3_SPRINT_WORDS, weight: 2 },
  'niveau-4': { chunks: NIVEAU_4_ALPHABET_WORDS, weight: 2 },
};

/** Reference key sets per tier (for UI labels). */
export const TIER_KEY_SETS: Record<string, readonly string[]> = {
  grundniveau: [...LEVEL_6_KEYS, ' '],
  'niveau-1': NIVEAU_1_UNLOCKED_KEYS,
  'niveau-2': NIVEAU_2_UNLOCKED_KEYS,
  'niveau-3': NIVEAU_3_UNLOCKED_KEYS,
  'niveau-4': NIVEAU_4_UNLOCKED_KEYS,
};

export const CHECKIN_DUE_MS = 7 * 24 * 60 * 60 * 1000;
const CHECKIN_PASSAGE_CHARS = 400;

/** Union af unlockedKeys kun fra gennemførte trin (ikke aktivt trin). */
export function getSolidUnlockedKeys(user: UserProfile): string[] {
  const keys = new Set<string>();
  for (const id of user.completedTrinIds) {
    const trin = getTrin(id);
    if (!trin) continue;
    for (const k of trin.unlockedKeys) keys.add(k);
  }
  return [...keys].sort();
}

export function solidKeyFingerprint(keys: string[]): string {
  return [...keys].sort().join('');
}

function solidCheckInSources(
  user: UserProfile,
): Array<{ chunks: string[]; weight: number }> {
  const solidKeys = getSolidUnlockedKeys(user);
  const out: Array<{ chunks: string[]; weight: number }> = [];
  for (const tier of TIER_ORDER) {
    const src = WORD_SOURCES[tier];
    if (!src) continue;
    const chunks = src.chunks.filter((c) =>
      textUsesOnlyKeys(c, solidKeys),
    );
    if (chunks.length > 0) {
      out.push({ chunks, weight: src.weight });
    }
  }
  return out;
}

export function buildCheckInPassage(
  user: UserProfile,
  seed: number,
): { text: string; solidKeys: string[] } {
  const solidKeys = getSolidUnlockedKeys(user);
  const sources = solidCheckInSources(user);
  if (sources.length === 0 || solidKeys.length === 0) {
    return { text: '', solidKeys };
  }
  const generate = interleaved(sources, CHECKIN_PASSAGE_CHARS);
  const text = generate({ seed });
  return { text, solidKeys };
}

export function isWpmCheckInEligible(user: UserProfile): boolean {
  const keys = getSolidUnlockedKeys(user);
  if (keys.length === 0) return false;
  return solidCheckInSources(user).some((s) => s.chunks.length > 0);
}

export function latestWpmCheckIn(
  user: UserProfile,
): WpmCheckIn | undefined {
  return user.wpmCheckIns?.[0];
}

export function isWpmCheckInDue(user: UserProfile): boolean {
  if (!isWpmCheckInEligible(user)) return false;
  const latest = latestWpmCheckIn(user);
  if (!latest) return true;
  return (
    Date.now() - new Date(latest.takenAt).getTime() >= CHECKIN_DUE_MS
  );
}

export function daysSinceLastCheckIn(user: UserProfile): number | null {
  const latest = latestWpmCheckIn(user);
  if (!latest) return null;
  const ms = Date.now() - new Date(latest.takenAt).getTime();
  return Math.floor(ms / (24 * 60 * 60 * 1000));
}

export function wpmCheckInDelta(user: UserProfile): number | null {
  const checkIns = user.wpmCheckIns ?? [];
  if (checkIns.length < 2) return null;
  return checkIns[0]!.wpm - checkIns[1]!.wpm;
}

export interface WpmCheckInComparison {
  vsPreviousWpm: number | null;
  vsPreviousAccPp: number | null;
  vsBaselineWpm: number | null;
  vsBaselineAccPp: number | null;
}

export function compareWpmCheckIn(
  checkIn: WpmCheckIn,
  previous?: WpmCheckIn,
  baseline?: BaselineSnapshot,
): WpmCheckInComparison {
  return {
    vsPreviousWpm: previous ? checkIn.wpm - previous.wpm : null,
    vsPreviousAccPp: previous
      ? (checkIn.accuracy - previous.accuracy) * 100
      : null,
    vsBaselineWpm: baseline ? checkIn.wpm - baseline.wpm : null,
    vsBaselineAccPp: baseline
      ? (checkIn.accuracy - baseline.accuracy) * 100
      : null,
  };
}

export function formatWpmDelta(delta: number | null): string {
  if (delta === null) return 'Første test';
  if (delta > 0) return `+${Math.round(delta)} wpm`;
  if (delta < 0) return `${Math.round(delta)} wpm`;
  return 'Samme som sidst';
}

export function formatAccDeltaPp(deltaPp: number | null): string {
  if (deltaPp === null) return '—';
  if (deltaPp > 0) return `+${Math.round(deltaPp)} procentpoint`;
  if (deltaPp < 0) return `${Math.round(deltaPp)} procentpoint`;
  return 'Uændret';
}

/** Kort beskrivelse af tast-repertoire til UI. */
export function solidRepertoireLabel(user: UserProfile): string {
  const n = getSolidUnlockedKeys(user).filter((k) => k !== ' ').length;
  return `${n} taster · kun gennemførte trin`;
}
